import { ArrowUpRight } from 'lucide-react'
import Reveal from './Reveal'

interface Project {
  name: string
  category: string
  description: string
  url: string
  domain: string
  image: string
}

const PROJECTS: Project[] = [
  {
    name: 'Fidah Jewellery',
    category: 'Online store',
    description: 'An online jewellery store built to show the pieces off and turn browsers into buyers.',
    url: 'https://fidah-store.pages.dev',
    domain: 'fidah-store.pages.dev',
    image: '/portfolio/fidah.jpg',
  },
  {
    name: 'Studio Form',
    category: 'Design studio',
    description: 'A bold editorial site that makes visitors slow down, read, and remember the name.',
    url: 'https://studio-form-kimi.pages.dev',
    domain: 'studio-form-kimi.pages.dev',
    image: '/portfolio/studio-form.jpg',
  },
  {
    name: 'Thalir OT',
    category: 'Health · NDIS',
    description: 'A warm, referral-focused site for a home-based occupational therapy practice.',
    url: 'https://thalir.pages.dev',
    domain: 'thalir.pages.dev',
    image: '/portfolio/thalir-ot.jpg',
  },
  {
    name: 'Correct Conveyancing',
    category: 'Property',
    description: 'A clean, trustworthy conveyancing site, written to earn the phone call.',
    url: 'https://clair-real-estate-demo.pages.dev',
    domain: 'clair-real-estate-demo.pages.dev',
    image: '/portfolio/correct-conveyancing.jpg',
  },
]

const scrollToContact = () => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })

const ProjectCard = ({ project }: { project: Project }) => (
  <a
    href={project.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group flex flex-col overflow-hidden rounded-xl2 border border-[var(--line)] bg-paper shadow-soft transition-all duration-300 hover:-translate-y-1.5 hover:shadow-card"
  >
    {/* Faux browser chrome */}
    <div className="flex items-center gap-2 border-b border-[var(--line)] bg-cream-deep/50 px-4 py-2.5">
      <span className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink/15" />
      </span>
      <span className="ml-2 truncate rounded-md bg-paper px-3 py-1 text-[0.72rem] font-medium text-ink-faint ring-1 ring-[var(--line)]">
        {project.domain}
      </span>
    </div>

    {/* Screenshot */}
    <div className="relative aspect-[16/10] overflow-hidden bg-cream-deep">
      <img
        src={project.image}
        alt={`${project.name} website built by CrateWeb`}
        width={1200}
        height={750}
        loading="lazy"
        className="h-full w-full object-cover object-top transition-transform duration-500 ease-out group-hover:scale-[1.04]"
      />
    </div>

    {/* Meta */}
    <div className="flex flex-1 flex-col p-6">
      <div className="flex items-center justify-between gap-3">
        <span className="rounded-full bg-gold/10 px-3 py-1 text-[0.72rem] font-semibold uppercase tracking-wider text-gold">
          {project.category}
        </span>
        <span className="inline-flex items-center gap-1 text-[0.82rem] font-semibold text-ink-faint transition-colors group-hover:text-gold">
          Visit site
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
      <h3 className="font-display mt-4 text-xl font-bold text-ink">{project.name}</h3>
      <p className="mt-2 text-[0.92rem] leading-relaxed text-ink-soft">{project.description}</p>
    </div>
  </a>
)

const Portfolio = () => {
  return (
    <section id="work" className="relative py-24 lg:py-32">
      <div className="mx-auto max-w-container px-5 lg:px-8">
        <Reveal className="max-w-2xl">
          <h2 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            Recent work for Melbourne businesses.
          </h2>
          <p className="mt-6 text-lg text-ink-soft">
            A few sites I have built and shipped recently, across retail, health, design, and property.
            Every one is live, fast, and owned by the business.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:gap-8">
          {PROJECTS.map((p, i) => (
            <Reveal key={p.name} delay={(i % 2) * 0.08}>
              <ProjectCard project={p} />
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-12">
          <div className="flex flex-col items-start gap-4 rounded-xl2 border border-[var(--line)] bg-cream-deep/40 p-7 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-lg text-ink-soft">
              <span className="font-semibold text-ink">Want one like these?</span> Most builds go live within a week.
            </p>
            <button onClick={scrollToContact} className="btn btn-primary whitespace-nowrap">
              Get a fixed-price quote
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

export default Portfolio
